import React, { useState } from 'react';

const Contact = ({ data, onNavigate }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate form submission
        try {
            // Here you would typically send the data to your backend or email service
            console.log('Form submitted:', formData);

            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            setIsSubmitted(true);

            // Reset form after 3 seconds
            setTimeout(() => {
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });
                setIsSubmitted(false);
            }, 3000);

        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackToHome = () => {
        if (onNavigate) {
            onNavigate('home');
        }
    };

    return (
        <section className="py-20 bg-stone-50 min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-stone-800 mb-6">
                        Get In Touch
                    </h2>
                    <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
                        Ready to take the first step? Contact me to schedule a consultation or ask any questions about therapy services.
                    </p>
                </div>

                {isSubmitted && (
                    <div className="max-w-2xl mx-auto mb-8 bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                        <div className="flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-green-800 mb-2">Message Sent Successfully!</h3>
                        <p className="text-green-700">Thank you for reaching out. I'll get back to you within 24 hours.</p>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    {/* Contact Information */}
                    <div>
                        <h3 className="text-2xl font-semibold text-stone-800 mb-8">Contact Information</h3>

                        <div className="space-y-8">
                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-stone-800 mb-1">Phone</h4>
                                    <a
                                        href={`tel:${data.phone}`}
                                        className="text-stone-600 hover:text-stone-800 transition-colors"
                                    >
                                        {data.phone}
                                    </a>
                                    <p className="text-stone-500 text-sm mt-1">Call or text for appointments</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center">
                                    <svg className="w-6 h-6 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-stone-800 mb-1">Email</h4>
                                    <a
                                        href={`mailto:${data.email}`}
                                        className="text-stone-600 hover:text-stone-800 transition-colors"
                                    >
                                        {data.email}
                                    </a>
                                    <p className="text-stone-500 text-sm mt-1">I typically respond within 24 hours</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-stone-800 mb-1">Address</h4>
                                    <p className="text-stone-600">{data.address}</p>
                                    <p className="text-stone-500 text-sm mt-1">Private, comfortable office setting</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-stone-800 mb-1">Office Hours</h4>
                                    <p className="text-stone-600">{data.hours}</p>
                                    <p className="text-stone-500 text-sm mt-1">Emergency consultations available</p>
                                </div>
                            </div>
                        </div>

                        {/* Map Placeholder */}
                        <div className="mt-12 w-full h-64 bg-gradient-to-br from-stone-200 to-stone-300 rounded-xl flex items-center justify-center">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-stone-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                </div>
                                <p className="text-sm text-stone-600">Interactive Map</p>
                            </div>
                        </div>

                        {/* Back to Home Button */}
                        <div className="mt-8">
                            <button
                                onClick={handleBackToHome}
                                className="flex items-center text-stone-600 hover:text-stone-800 transition-colors"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Back to Home
                            </button>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div>
                        <h3 className="text-2xl font-semibold text-stone-800 mb-8">Send a Message</h3>

                        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100">
                            {/* ... keep all your existing form fields exactly as they are ... */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors"
                                        placeholder="Your name"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors"
                                        placeholder="your.email@example.com"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors"
                                        placeholder="(555) 123-4567"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="subject" className="block text-sm font-medium text-stone-700 mb-2">
                                        Subject
                                    </label>
                                    <select
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors"
                                    >
                                        <option value="">Select a subject</option>
                                        <option value="consultation">Initial Consultation</option>
                                        <option value="individual">Individual Therapy</option>
                                        <option value="couples">Couples Counseling</option>
                                        <option value="family">Family Therapy</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className="mb-6">
                                <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                                    Message *
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    rows={6}
                                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors resize-none"
                                    placeholder="Tell me a bit about what brings you here and how I can help..."
                                ></textarea>
                            </div>

                            <div className="mb-6">
                                <div className="flex items-start space-x-3">
                                    <input
                                        type="checkbox"
                                        id="privacy"
                                        className="mt-1 h-4 w-4 text-amber-600 focus:ring-amber-500 border-stone-300 rounded"
                                        required
                                    />
                                    <label htmlFor="privacy" className="text-sm text-stone-600">
                                        I understand that this form is not for crisis situations. If you're experiencing a mental health emergency,
                                        please call 911 or the National Suicide Prevention Lifeline at 988.
                                    </label>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-stone-800 text-white py-4 px-6 rounded-lg font-medium hover:bg-stone-700 transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Additional Information */}
                <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-stone-100">
                        <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-stone-800 mb-3">Insurance Accepted</h4>
                        <p className="text-stone-600 text-sm">Most major insurance plans accepted. Contact for verification.</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-stone-100">
                        <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-stone-800 mb-3">Confidential</h4>
                        <p className="text-stone-600 text-sm">All sessions and communications are completely confidential and secure.</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-stone-100 md:col-span-2 lg:col-span-1">
                        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a4 4 0 118 0v4m-4 8a2 2 0 100-4 2 2 0 000 4zm6 4v1a3 3 0 01-3 3H9a3 3 0 01-3-3v-1m8-6V9a4 4 0 00-8 0v3.5" />
                            </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-stone-800 mb-3">Flexible Options</h4>
                        <p className="text-stone-600 text-sm">In-person and virtual sessions available to fit your schedule.</p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;